/**
 * Sentry SDK initialisation — browser half of issue #25.
 *
 * Mirrors the backend hardening in `backend/app/core/observability.py`:
 *
 *   - No-op when VITE_SENTRY_DSN is empty (default in dev / preview).
 *   - PII scrubbing in `beforeSend`: strips request bodies, masks
 *     sensitive query params, drops the `User.email` field. Patrimony +
 *     identity data is the most sensitive content this SPA holds; nothing
 *     client-personal must leak into Sentry.
 *   - Conservative sample rate: 5% on production, 100% on staging, off
 *     in development.
 *   - Tied to `import.meta.env.MODE` for environment tagging.
 *
 * The DSN is a build-arg (Vite injects it at build time via the
 * frontend Dockerfile), not a runtime config. Per CLAUDE.md §Stack:
 * "Variables VITE_* injectées au build (build args Docker), pas au
 * runtime".
 */

import type { ErrorEvent } from "@sentry/react";

import { config } from "./config";

/** Query-string keys we mask before egress. */
const SENSITIVE_QUERY_KEYS: ReadonlySet<string> = new Set([
  "email",
  "token",
  "password",
  "otp",
  "code",
  "recovery_code",
  "invitation_token",
  "refresh_token",
  "secret",
  "api_key",
]);

const REDACTED = "[REDACTED]";

function tracesSampleRateForEnv(env: string): number {
  if (env === "production") return 0.05;
  if (env === "staging") return 1.0;
  return 0.0; // dev: no traces
}

/**
 * Mask sensitive keys in a URL-encoded query string. Accepts malformed
 * input — returns the original on parse failure rather than crashing.
 *
 * NOTE: we parse with `URLSearchParams` directly (NOT `URL`) so a bare
 * `key=value&key2=value2` string is treated as a query string, not a
 * pathname. Sentry's `request.query_string` field is bare — without
 * the leading `?` — so this is the right primitive.
 */
function scrubQueryString(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  try {
    const params = new URLSearchParams(raw);
    let mutated = false;
    for (const key of [...params.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        params.set(key, REDACTED);
        mutated = true;
      }
    }
    return mutated ? params.toString() : raw;
  } catch {
    return raw;
  }
}

/**
 * Sentry `beforeSend` hook. Mutates and returns the event; returns
 * `null` ONLY if the scrubber itself throws (defence in depth — never
 * crash error reporting because of a scrubber bug).
 */
export function beforeSend(event: ErrorEvent): ErrorEvent | null {
  try {
    // Drop the body wholesale — anything the SPA POSTs may carry PII
    // (person name, IBAN, etc). Treat `event.request` loosely (the
    // upstream type is narrower than what the SDK actually serialises).
    if (event.request) {
      const req = event.request as Record<string, unknown>;
      delete req.data;
      const scrubbed = scrubQueryString(req.query_string as string | undefined);
      if (scrubbed === undefined) {
        delete req.query_string;
      } else {
        req.query_string = scrubbed;
      }
      if ("cookies" in req) {
        req.cookies = REDACTED;
      }
    }

    // `extra` carries free-form developer context; drop the whole bag.
    if (event.extra) {
      delete (event as unknown as Record<string, unknown>).extra;
    }

    // Keep only the user id, never the email/IP/username.
    if (event.user) {
      const id = event.user.id;
      if (id) {
        event.user = { id };
      } else {
        delete (event as unknown as Record<string, unknown>).user;
      }
    }

    return event;
  } catch {
    // Scrubber bug → drop the event entirely so we never leak the
    // unfiltered original. Backend logs the issue separately.
    return null;
  }
}

/**
 * Initialise Sentry. Called from `main.tsx` BEFORE `createRoot`.
 *
 * No-op when `VITE_SENTRY_DSN` is empty (default in dev). Idempotent —
 * Sentry's SDK swallows repeated init.
 */
export async function initSentry(): Promise<void> {
  const dsn = config.sentryDsn?.trim();
  if (!dsn) return;

  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn,
    environment: config.appEnv,
    release: import.meta.env.VITE_RELEASE_SHA || undefined,
    tracesSampleRate: tracesSampleRateForEnv(config.appEnv),
    // Sentry's own "scrub default PII" switch — belt + the beforeSend
    // suspenders.
    sendDefaultPii: false,
    beforeSend,
    integrations: [Sentry.browserTracingIntegration()],
  });
}
